import { type ReactNode, type Ref } from "react";
import { ChevronIcon, FileIcon, FolderIcon } from "vega-icons";

import { ThemedButton } from "@vega-core/components/Buttons/ThemedButton";
import { ThemedIconButton } from "@vega-core/components/Buttons/ThemedIconButton";
import { m } from "@vega-core/paraglide/messages";
import { cnTwMerge } from "@vega-core/utils/cnTwMerge";

import { isViewableInApp } from "../../utils/dataRoomUtils";

import type { DataRoomFileTreeNode } from "./types";

const DEPTH_INDENT_PX = 20;

type DataRoomFileTreeCellProps<TNode extends DataRoomFileTreeNode> = {
  node: TNode;
  depth: number;
  isCollapsed: boolean;
  onToggleCollapse: () => void;
  onOpenPdf?: (node: TNode) => void;
  onOpenFile?: (node: TNode) => void;
  actionsSlot?: ReactNode;
  badgeSlot?: ReactNode;
  className?: string;
  contentClassName?: string;
  indentPx?: number;
  isDropTarget?: boolean;
  tableCellRef?: Ref<HTMLTableCellElement>;
  cellTestId?: string;
  collapseToggleTestId?: string;
  fileLinkTestId?: string;
  nameTestId?: string;
  folderToggleAriaLabel?: string;
  title?: string;
};

export function DataRoomFileTreeCell<TNode extends DataRoomFileTreeNode>({
  node,
  depth,
  isCollapsed,
  onToggleCollapse,
  onOpenPdf,
  onOpenFile,
  actionsSlot,
  badgeSlot,
  className,
  contentClassName,
  indentPx = DEPTH_INDENT_PX,
  isDropTarget = false,
  tableCellRef,
  cellTestId,
  collapseToggleTestId,
  fileLinkTestId,
  nameTestId,
  folderToggleAriaLabel,
  title,
}: DataRoomFileTreeCellProps<TNode>) {
  const isFolder = node.type === "folder";
  const rowBackgroundClass = isFolder
    ? "bg-background/secondary"
    : "bg-background/primary";
  const nameClassName = cnTwMerge(
    "min-w-0 truncate",
    isFolder ? "body-base-medium" : "body-base-regular",
  );
  const hasFile = node.type === "file" && Boolean(node.file?.id);

  function handleOpenFileClick() {
    onOpenFile?.(node);
  }

  let nameElement: ReactNode;

  if (hasFile && isViewableInApp(node) && onOpenPdf) {
    nameElement = (
      <ThemedButton
        className={cnTwMerge(
          nameClassName,
          "w-auto rounded-sm px-0.5 text-brand-default no-underline hover:underline",
        )}
        dataTestId={fileLinkTestId ?? `file-link-${node.id}`}
        onClick={() => onOpenPdf(node)}
        title={title ?? node.name}
        truncate
        variant="text"
      >
        {node.name}
      </ThemedButton>
    );
  } else if (hasFile && onOpenFile) {
    nameElement = (
      <ThemedButton
        className={cnTwMerge(
          nameClassName,
          "w-auto rounded-sm px-0.5 text-brand-default no-underline hover:underline",
        )}
        dataTestId={fileLinkTestId ?? `file-link-${node.id}`}
        onClick={handleOpenFileClick}
        title={title ?? node.name}
        truncate
        variant="text"
      >
        {node.name}
      </ThemedButton>
    );
  } else {
    nameElement = (
      <span
        className={cnTwMerge(nameClassName, "text-text/primary")}
        data-testid={nameTestId ?? `tree-row-name-${node.id}`}
        title={title ?? node.name}
      >
        {node.name}
      </span>
    );
  }

  return (
    <td
      className={cnTwMerge(
        "h-9 border-b border-border/primary p-0",
        rowBackgroundClass,
        isDropTarget && "bg-background/brand/opacity",
        className,
      )}
      data-data-room-column="tree"
      data-node-id={node.id}
      data-testid={cellTestId}
      ref={tableCellRef}
    >
      <div
        className={cnTwMerge(
          "flex items-center gap-1.5 px-2",
          contentClassName,
        )}
      >
        <span className="shrink-0" style={{ width: depth * indentPx }} />
        {isFolder ? (
          <ThemedIconButton
            aria-expanded={!isCollapsed}
            ariaLabel={
              folderToggleAriaLabel ??
              (isCollapsed
                ? m.coInvestments_dataRoom_expandFolder({
                    folderName: node.name,
                  })
                : m.coInvestments_dataRoom_collapseFolder({
                    folderName: node.name,
                  }))
            }
            className="h-4 w-4 shrink-0"
            dataTestId={collapseToggleTestId ?? `folder-toggle-${node.id}`}
            icon={
              <ChevronIcon
                className={cnTwMerge(
                  "size-2.5 transition-transform duration-150",
                  isCollapsed ? "-rotate-90" : "rotate-0",
                )}
              />
            }
            onClick={onToggleCollapse}
            size="extraSmall"
            variant="noBackground"
          />
        ) : (
          <span className="size-4 shrink-0" />
        )}
        <span
          aria-hidden="true"
          className="flex size-3.5 shrink-0 items-center justify-center text-icon/secondary"
        >
          {isFolder ? (
            <FolderIcon className="size-3.5" />
          ) : (
            <FileIcon className="size-3.5" />
          )}
        </span>
        {nameElement}
        {badgeSlot}
        {actionsSlot}
      </div>
    </td>
  );
}
